import React, { useEffect, useState, useCallback, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { IconCopy, IconCheck } from '@tabler/icons-react';
import { functions } from '../firebase';
import './ReferralInvitesList.css';

function statusLabel(status) {
  switch (status) {
    case 'pending': return 'Waiting for your friend to open the link';
    case 'accepted_new': return 'Your friend joined — their map is on the way';
    case 'gifted': return 'Gift map sent to your friend';
    case 'blocked_existing': return 'Your friend already has an account';
    default: return status;
  }
}

// The inviter's list of invites, shown on the /diamonds page. Served by the
// listMyReferralInvites callable (Admin SDK) so it works without deploying the referralInvites
// read rule; polled so status transitions show up. The inviter's +5 is granted server-side
// when the referral map completes; when polling sees a reward flip to "earned" we just
// refresh the credits badge via onCreditsUpdated.
function ReferralInvitesList({ user, onCreditsUpdated = null }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const seenEarnedIdsRef = useRef(new Set());
  const seedDoneRef = useRef(false);

  const loadInvites = useCallback(async () => {
    if (!user) return;
    try {
      const listFn = httpsCallable(functions, 'listMyReferralInvites');
      const res = await listFn();
      setInvites(res.data?.invites || []);
    } catch (err) {
      console.error('listMyReferralInvites failed:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) { setLoading(false); return undefined; }
    loadInvites();
    const timer = setInterval(loadInvites, 12000); // reflect accepted/gifted transitions
    return () => clearInterval(timer);
  }, [user, loadInvites]);

  // When polling shows an invite's reward newly flipped to "earned", the +5 has already
  // landed server-side; refresh the nav badge so the new balance shows up. The first load
  // only seeds the seen-set (those rewards are already reflected in the balance).
  useEffect(() => {
    if (loading) return;
    const seen = seenEarnedIdsRef.current;
    const seeding = !seedDoneRef.current;
    seedDoneRef.current = true;
    let sawNew = false;
    invites.forEach((inv) => {
      if (inv.referralReward !== 'earned' || seen.has(inv.id)) return;
      seen.add(inv.id);
      if (!seeding) sawNew = true;
    });
    if (sawNew && onCreditsUpdated) onCreditsUpdated();
  }, [invites, loading, onCreditsUpdated]);

  const copyLink = async (inv) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.id}`);
      setCopiedId(inv.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch (_) { /* clipboard unavailable */ }
  };

  if (!user || loading || invites.length === 0) return null;

  return (
    <div className="referral-invites reward-card">
      <div className="reward-content">
        <h4 className="referral-invites-title">Your invites</h4>
        <div className="referral-invites-list">
          {invites.map((inv) => (
            <div key={inv.id} className={`referral-invite-row status-${inv.status}`}>
              <div className="referral-invite-main">
                <span className="referral-invite-style">
                  {inv.styleName || 'Custom map'}{inv.isGift ? ' · gift' : ''}
                </span>
                <span className="referral-invite-status">{statusLabel(inv.status)}</span>
              </div>
              <div className="referral-invite-actions">
                {inv.status === 'pending' && (
                  <button className="referral-invite-btn" onClick={() => copyLink(inv)}>
                    {copiedId === inv.id ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    {copiedId === inv.id ? 'Copied' : 'Copy link'}
                  </button>
                )}
                {inv.status === 'accepted_new' && (
                  <span className="referral-invite-earned">
                    {inv.referralReward === 'earned' ? '+5 💎 earned' : 'In progress'}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ReferralInvitesList;
